import { Pets } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useGetPetInpatientStay } from "../../../../../../hooks/useGetPetInpatientStay";
import AddInpatientStay from "../../../../components/forms/AddInpatientStay";
import { single_box_view_title_size } from "../../SingleBoxView";
import InpatientStayList from "./InpatientStayList";
import { MenuProps } from "./MedicalRecords";
import HorizontalStack from "../../../../components/wrappers/HorizontalStack";
import useCheckPath from "../../../../../../hooks/useCheckPath";
export const inpatient_stay_id_param = "inpatient_stay_id";

export default function InpatientStay({ pets = [] }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const petId =
    searchParams.get(inpatient_stay_id_param) ??
    (pets.length > 0 ? pets[0].id : null);

  const { data, isLoading, error } = useGetPetInpatientStay(petId);

  if (isLoading) {
    return (
      <Wrapper petId={petId} pets={pets} data={data}>
        <LinearProgress />
      </Wrapper>
    );
  } else if (error) {
    return (
      <Wrapper petId={petId} pets={pets} data={data}>
        <Typography color="error">{t("FailedToFetchStays")}</Typography>
      </Wrapper>
    );
  } else if (pets?.length == 0) {
    return (
      <Wrapper petId={petId} pets={pets} data={data}>
        <Typography color="GrayText" variant="subtitle2">
          {t("NoPetsInBox")}
        </Typography>
      </Wrapper>
    );
  }

  return (
    <Wrapper petId={petId} pets={pets} data={data}>
      <InpatientStayList pets={pets} data={data} />
    </Wrapper>
  );
}

function Wrapper({ children, pets = [], petId = null, data = null }) {
  const { t } = useTranslation();
  const isArchived = useCheckPath();
  return (
    <Card>
      <CardContent>
        {pets?.length > 0 ? (
          <>
            <HorizontalStack>
              <Typography variant={single_box_view_title_size}>
                {t("InpatientStay")}
              </Typography>
              {isArchived ? null : <AddInpatientStay selectedPetId={petId} inpatientStay={data} />}
            </HorizontalStack>
            <Box mb={2} mt={1}>
              <PetPicker pets={pets} />
            </Box>
          </>
        ) : (
          <Typography variant={single_box_view_title_size} gutterBottom>
            {t("InpatientStay")}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function PetPicker({ pets = [] }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPet, setSelectedPet] = React.useState("");

  function handleChange(pet, id) {
    setSelectedPet(pet);
    const newParams = new URLSearchParams(searchParams);
    newParams.set(inpatient_stay_id_param, id);
    setSearchParams(newParams);
  }

  function getPetById(id) {
    return pets.filter((pet) => String(pet.id) === String(id));
  }

  React.useEffect(() => {
    const paramId = searchParams.get(inpatient_stay_id_param);
    if (paramId != null) {
      handleChange(getPetById(paramId)[0]?.name, paramId);
    } else if (!selectedPet?.name && pets?.length > 0) {
      handleChange(pets[0].name, pets[0].id);
    }
  }, [pets]);

  if (pets.length < 2) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
        <Pets />
      </Avatar>
      <Box sx={{ flexGrow: 1 }}>
        <FormControl sx={{ width: { xs: "100%", sm: "300px" } }}>
          <InputLabel id="pets-medical-data">{t("ChoosePet")}</InputLabel>
          <Select
            labelId="pets-medical-data"
            value={selectedPet}
            input={<OutlinedInput label={t("ChoosePet")} />}
            MenuProps={MenuProps}
          >
            {pets.map(({ name, id }) => (
              <MenuItem
                key={id}
                value={name}
                onClick={() => handleChange(name, id)}
              >
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Stack>
  );
}
